/**
 * FunnelCanvas — pannable container for funnel nodes and edges.
 * Handles drag-to-pan of the viewport and node dragging.
 */
import { useState, useCallback, useRef, useEffect } from "react";
import { ZoomIn, ZoomOut, Maximize2 } from "lucide-react";
import type { FunnelNodeData, FunnelEdgeData } from "./funnel-types";
import FunnelNode from "./funnel-node";
import FunnelEdges from "./funnel-edges";
import { useFunnelDrag } from "./use-funnel-drag";

// ── Layout constants ───────────────────────────────────

const CANVAS_HEIGHT = 520;
const NODE_RENDER_WIDTH = 180;
const NODE_RENDER_HEIGHT = 180;
const FIT_PADDING = 40;

// ── Fit viewport helper ────────────────────────────────

const computeFitViewport = (
  nodes: FunnelNodeData[],
  containerWidth: number
): { zoom: number; pan: { x: number; y: number } } => {
  if (nodes.length === 0) return { zoom: 1, pan: { x: 0, y: 0 } };

  const minX = Math.min(...nodes.map((n) => n.position.x));
  const minY = Math.min(...nodes.map((n) => n.position.y));
  const maxX = Math.max(...nodes.map((n) => n.position.x + NODE_RENDER_WIDTH));
  const maxY = Math.max(...nodes.map((n) => n.position.y + NODE_RENDER_HEIGHT));

  const contentW = maxX - minX + FIT_PADDING * 2;
  const contentH = maxY - minY + FIT_PADDING * 2;

  const scaleX = containerWidth / contentW;
  const scaleY = CANVAS_HEIGHT / contentH;
  const zoom = Math.min(scaleX, scaleY, 1);

  const panX = (containerWidth / zoom - (maxX - minX)) / 2 - minX;
  const panY = (CANVAS_HEIGHT / zoom - (maxY - minY)) / 2 - minY;

  return { zoom: Math.max(zoom, 0.25), pan: { x: panX, y: panY } };
};

// ── Component ──────────────────────────────────────────

interface FunnelCanvasProps {
  nodes: FunnelNodeData[];
  edges: FunnelEdgeData[];
  onNodesChange: (nodes: FunnelNodeData[]) => void;
}

export default function FunnelCanvas({ nodes, edges, onNodesChange }: FunnelCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);

  const handleUpdatePosition = useCallback(
    (nodeId: string, position: { x: number; y: number }) => {
      const updated = nodes.map((n) => (n.id === nodeId ? { ...n, position } : n));
      onNodesChange(updated);
    },
    [nodes, onNodesChange]
  );

  const { isDragging, dragNodeId, onPointerDown, onPointerMove, onPointerUp } = useFunnelDrag(
    nodes,
    handleUpdatePosition
  );

  // Background pan (when not dragging a node)
  const handleBgPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (isDragging) return;
      panStartRef.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
      setIsPanning(true);
    },
    [isDragging, pan]
  );

  const handleBgPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (isDragging) {
        onPointerMove(e);
        return;
      }
      const ps = panStartRef.current;
      if (!ps) return;

      const dx = e.clientX - ps.x;
      const dy = e.clientY - ps.y;
      setPan({ x: ps.panX + dx, y: ps.panY + dy });
    },
    [isDragging, onPointerMove]
  );

  const handleBgPointerUp = useCallback(() => {
    panStartRef.current = null;
    setIsPanning(false);
    onPointerUp();
  }, [onPointerUp]);

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.15, 2));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.15, 0.4));
  const handleFitView = useCallback(() => {
    const width = containerRef.current?.clientWidth ?? 900;
    const fit = computeFitViewport(nodes, width);
    setZoom(fit.zoom);
    setPan(fit.pan);
  }, [nodes]);

  // Auto-fit when nodes first load
  const hasAutoFit = useRef(false);
  useEffect(() => {
    if (nodes.length > 0 && !hasAutoFit.current) {
      hasAutoFit.current = true;
      // Allow container to measure before fitting
      requestAnimationFrame(() => handleFitView());
    }
  }, [nodes, handleFitView]);

  return (
    <div className="relative w-full h-[520px] bg-[#0d0b0a] rounded-xl border border-white/[0.06] overflow-hidden">
      {/* Grid background */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `
            radial-gradient(circle, rgba(255,255,255,0.03) 1px, transparent 1px)
          `,
          backgroundSize: "24px 24px",
          backgroundPosition: `${pan.x}px ${pan.y}px`,
        }}
      />

      {/* Zoom controls */}
      <div className="absolute top-3 right-3 z-50 flex items-center gap-1 bg-black/60 border border-white/10 rounded-lg p-1">
        <button
          onClick={handleZoomIn}
          className="p-1.5 rounded hover:bg-white/10 text-white/50 hover:text-white transition-colors"
          title="Przybliż"
        >
          <ZoomIn className="w-3.5 h-3.5" />
        </button>
        <span className="text-[10px] text-white/40 px-1 min-w-[36px] text-center">
          {Math.round(zoom * 100)}%
        </span>
        <button
          onClick={handleZoomOut}
          className="p-1.5 rounded hover:bg-white/10 text-white/50 hover:text-white transition-colors"
          title="Oddal"
        >
          <ZoomOut className="w-3.5 h-3.5" />
        </button>
        <div className="w-px h-4 bg-white/10" />
        <button
          onClick={handleFitView}
          className="p-1.5 rounded hover:bg-white/10 text-white/50 hover:text-white transition-colors"
          title="Dopasuj widok"
        >
          <Maximize2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Canvas area */}
      <div
        ref={containerRef}
        className="absolute inset-0"
        style={{ cursor: isPanning ? "grabbing" : isDragging ? "grabbing" : "default" }}
        onPointerDown={handleBgPointerDown}
        onPointerMove={handleBgPointerMove}
        onPointerUp={handleBgPointerUp}
        onPointerLeave={handleBgPointerUp}
      >
        <div
          className="relative w-full h-full"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: "0 0",
          }}
        >
          {/* Edges layer */}
          <FunnelEdges nodes={nodes} edges={edges} />

          {/* Nodes layer */}
          {nodes.map((node) => (
            <FunnelNode
              key={node.id}
              node={node}
              isBeingDragged={dragNodeId === node.id}
              onPointerDown={onPointerDown}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
